import { FileText } from "lucide-react";
import { RefObject, useState } from "react";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import {
	BlockType,
	type LoanTapePreviewColumn,
	UIDocumentRef,
	UIMessage,
	SelectedVersions,
} from "./Chat/Chat.types";
import { visibleMessages } from "./Chat/Chat.utils";
import AssistantMessage from "./AssistantMessage";
import { katexOptions, normalizeMathDelimiters } from "../utils/mathMarkdown";
import "./MessageList.css";

const MentionChips: React.FC<{ refs: UIDocumentRef[] }> = ({ refs }) => (
	<div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100">
		{refs.map((ref) => {
			const label = ref.ref_type === "deal"
				? `Deal: ${ref.filename || ref.deal_name || ref.doc_id}`
				: ref.filename || ref.deal_name || ref.doc_id;
			return (
				<span
					key={ref.doc_id}
					className="mention-chip"
				>
					<FileText size={11} className="flex-shrink-0" />
					<span className="truncate">{label}</span>
				</span>
			);
		})}
	</div>
);

interface MessageListProps {
	messages: UIMessage[];
	isLoading: boolean;
	/** Re-ask the question behind a failed answer (by the answer's UI id). */
	/** Regenerate an answer in place (DOP-436). */
	onRegenerateMessage?: (assistantId: string) => void;
	/** The message being generated right now, if any — the one that shows a cursor. */
	streamingMessageId?: string | null;
	/** Which version of each question the reader chose to look at. */
	selectedVersions?: SelectedVersions;
	onSelectVersion?: (questionMessageId: number, versionMessageId: number) => void;
	bottomRef: RefObject<HTMLDivElement | null>;
	chatId: string | null;
	chatType?: string | null;
	onOpenTrace?: (msgId: string) => void;
	onOpenEvidence?: (msgId: string) => void;
	/** Open an answer's context graph in the host, instead of navigating (DOP-492). */
	onOpenGraph?: (msgId: string) => void;
	/** The answer whose view the host has open, so its pill can show it. */
	openView?: { kind: "audit" | "graph"; msgId: string } | null;
	/** Column-lineage inspection counts, keyed by message id (session-only). */
	inspectionCounts?: Record<string, number>;
	onInspectColumn?: (msgId: string, column: LoanTapePreviewColumn) => void;
}

const MessageList: React.FC<MessageListProps> = ({
	messages,
	isLoading,
	onRegenerateMessage,
	streamingMessageId,
	selectedVersions,
	onSelectVersion,
	bottomRef,
	chatId,
	chatType,
	onOpenTrace,
	onOpenEvidence,
	onOpenGraph,
	openView,
	inspectionCounts,
	onInspectColumn,
}) => {
	const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

	const handleCopyToClipboard = async (text: string, messageId: string) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedMessageId(messageId);
			setTimeout(() => {
				setCopiedMessageId(null);
			}, 2000);
		} catch (err) {
			console.error("Failed to copy text: ", err);
		}
	};

	const extractTextFromMessage = (msg: UIMessage): string => {
		return msg.content_blocks
			.filter((b) => b.type === BlockType.TEXT)
			.map((b) => (b as { type: "text"; text: string }).text)
			.join("");
	};

	const visible = visibleMessages(messages, selectedVersions);

	return (
			<div className="w-full space-y-6 pb-6">
				{visible.map((msg) => {
					// By identity, not by position: a regenerated answer streams
					// into the slot of the answer it replaces, not at the end.
					const isStreamingThis =
						msg.role === "assistant" && isLoading && msg.id === streamingMessageId;

					return (
					<div key={msg.id} data-message-id={msg.id} data-role={msg.role} className="flex justify-center">
						<div
							className={`answer-column ${
								msg.role === "user" ? "flex justify-end" : "flex justify-start"
							}`}
						>
							{msg.role === "user" ? (
								/* The question, as an object on the page: a quiet grey fill and
								   a 12px radius per the Demo 1 mockup. The shadow went with the
								   white fill — a raised card needed an edge, a filled one
								   already has one. */
								<div className="inline-flex flex-col max-w-[70%] px-4 py-2.5 bg-surface-bubble rounded-xl overflow-hidden">
									<div
										data-message-content
										className="markdown-content break-words min-w-0 font-normal text-[16px] leading-[1.5] text-text-ink prose prose-sm max-w-none"
										style={{ fontFamily: '"Inter", "Inter Display", sans-serif' }}
									>
										{msg.content_blocks.map((block, index) => {
											if (block.type === BlockType.TEXT) {
												return (
													<ReactMarkdown key={index} remarkPlugins={[remarkGfm, remarkBreaks, [remarkMath, { singleDollarTextMath: true }]]} rehypePlugins={[[rehypeKatex, katexOptions]]}>
														{normalizeMathDelimiters(block.text)}
													</ReactMarkdown>
												);
											}
											return null;
										})}
									</div>
									{msg.documentRefs && msg.documentRefs.length > 0 && (
										<MentionChips refs={msg.documentRefs} />
									)}
								</div>
								) : (
									<AssistantMessage
										msg={msg}
										isStreamingThis={isStreamingThis}
										copiedMessageId={copiedMessageId}
										onCopy={handleCopyToClipboard}
										extractText={extractTextFromMessage}
										chatId={chatId}
										chatType={chatType}
										onOpenTrace={onOpenTrace}
										onOpenEvidence={onOpenEvidence}
										onOpenGraph={onOpenGraph}
										openView={
											openView && openView.msgId === msg.id
												? openView.kind
												: null
										}
										onRegenerate={
											onRegenerateMessage && !isLoading
												? () => onRegenerateMessage(msg.id)
												: undefined
										}
										selectedVersionId={
											msg.versions
												? selectedVersions?.[msg.versions.questionMessageId]
												: undefined
										}
										onSelectVersion={
											onSelectVersion && msg.versions
												? (versionId) =>
														onSelectVersion(msg.versions!.questionMessageId, versionId)
												: undefined
										}
										inspectionCount={inspectionCounts?.[msg.id] ?? 0}
										onInspectColumn={
											onInspectColumn
												? (column) => onInspectColumn(msg.id, column)
												: undefined
										}
									/>
								)}
							</div>
						</div>
					);
				})}
				<div ref={bottomRef} />
			</div>
		);
};

export default MessageList;
